import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Coins, Info, TrendingUp } from "lucide-react";
import { format, parseISO } from "date-fns";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StatCard } from "@/components/ui/stat-card";
import {
  CHART_AXIS_TICK,
  CHART_COLORS,
  CHART_TOOLTIP_STYLE,
  ChartCard
} from "@/components/ui/chart-card";
import { ChartEmptyState } from "@/components/ui/chart-empty-state";
import { OrgCostKpis } from "@/models/org-kpis";
import { formatters } from "@/utils/formatters";
import { ORIGIN_LABELS, countryName, formatUsd, labelOf } from "../org-kpis.meta";
import { BillCard } from "./BillCard";

interface CostTabProps {
  cost: OrgCostKpis;
}

const change = (then: number, next: number) => {
  if (then === 0) return next === 0 ? "No change" : "New cost";
  const pct = ((next - then) / then) * 100;
  return `${pct >= 0 ? "+" : ""}${formatters.formatPercentage(pct)} vs then`;
};

export function CostTab({ cost }: CostTabProps) {
  const { quality } = cost;
  const daily = cost.daily.map((d) => ({ ...d, label: format(parseISO(d.day), "MMM d") }));

  return (
    <div className="space-y-6">
      {!quality.ratesConfigured && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No Meta rates entered</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-3">
            Costs show as $0 until Meta’s rate card is entered.
            <Button asChild size="sm" variant="outline">
              <Link to="/pricing">
                Open Pricing <ArrowRight className="ml-1 h-3 w-3" />
              </Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="At the time"
          value={formatUsd(cost.then)}
          hint="Rules and rates in force on each day"
          icon={Coins}
        />
        <StatCard
          label="New pricing"
          value={formatUsd(cost.new)}
          hint="1 Oct 2026 rules, today's rates"
          icon={TrendingUp}
          tone="primary"
        />
        <StatCard
          label="Difference"
          value={`${cost.difference >= 0 ? "+" : ""}${formatUsd(cost.difference)}`}
          hint={change(cost.then, cost.new)}
          tone={cost.difference > 0 ? "warning" : "default"}
        />
        <StatCard
          label="Billed messages"
          value={formatters.formatNumber(cost.billable)}
          hint="Delivered or read, of a type Meta charges for from 1 Oct 2026"
        />
      </div>

      <BillCard bill={cost.bill} />

      <ChartCard
        title="Cost per day"
        description="What each day cost then, and what the same traffic costs under the new rules"
      >
        {daily.length === 0 ? (
          <ChartEmptyState
            icon={Coins}
            title="No billed messages in this period"
            description="Nothing delivered was of a paid type."
          />
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily} margin={{ left: 0, right: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={CHART_AXIS_TICK} tickLine={false} minTickGap={16} />
                <YAxis
                  tick={CHART_AXIS_TICK}
                  tickLine={false}
                  axisLine={false}
                  width={56}
                  tickFormatter={(v: number) => `$${v}`}
                />
                <Tooltip
                  contentStyle={CHART_TOOLTIP_STYLE}
                  cursor={{ fill: "hsl(var(--muted))" }}
                  formatter={(v) => formatUsd(Number(v))}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar
                  dataKey="then"
                  name="At the time"
                  fill={CHART_COLORS[1]}
                  radius={[3, 3, 0, 0]}
                />
                <Bar
                  dataKey="new"
                  name="New pricing"
                  fill={CHART_COLORS[0]}
                  radius={[3, 3, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartCard>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard title="By customer country" description="Meta prices by the recipient's country">
          <CostTable
            head="Country"
            rows={cost.byCountry.map((c) => ({
              key: c.country,
              label: countryName(c.country),
              badge: c.hasOwnRate ? undefined : "default rate",
              billable: c.billable,
              then: c.then,
              next: c.new
            }))}
          />
        </ChartCard>
        <ChartCard title="By where it came from">
          <CostTable
            head="Source"
            rows={cost.byOrigin.map((o) => ({
              key: o.origin,
              label: labelOf(ORIGIN_LABELS, o.origin),
              billable: o.billable,
              then: o.then,
              next: o.new
            }))}
          />
        </ChartCard>
      </div>

      <ChartCard title="How sure these numbers are">
        <ul className="space-y-2 text-sm">
          <QualityLine
            show
            text={`${formatters.formatNumber(quality.metaPricedMessages)} billed messages priced from Meta's own pricing data, ${formatters.formatNumber(quality.estimatedMessages)} estimated from what was sent.`}
          />
          <QualityLine
            show={quality.freeTierServiceMessages > 0}
            text={`${formatters.formatNumber(quality.freeTierServiceMessages)} service messages fell in Meta’s free tier: the first 1,000 a month on each phone number are free.`}
          />
          <QualityLine
            show={quality.freeEntryPointMessages > 0}
            text={`${formatters.formatNumber(quality.freeEntryPointMessages)} delivered messages were free: they fell inside the 72h window after a Click-to-WhatsApp ad.`}
          />
          <QualityLine
            show={quality.unconfirmedMessages > 0}
            text={`${formatters.formatNumber(quality.unconfirmedMessages)} messages have no delivery receipt yet and are not priced. Meta only charges delivered messages.`}
          />
          <QualityLine
            show={quality.unknownTemplateMessages > 0}
            warn
            text={`${formatters.formatNumber(quality.unknownTemplateMessages)} templates have no known category and are priced as marketing (the highest rate).`}
          />
          <QualityLine
            show={quality.unpricedMessages > 0}
            warn
            text={`${formatters.formatNumber(quality.unpricedMessages)} billed messages have no rate for their country and no default rate.`}
          />
          <QualityLine
            show={quality.unknownCountryMessages > 0}
            text={`${formatters.formatNumber(quality.unknownCountryMessages)} billed messages went to numbers with no known country; they use the default rate.`}
          />
          <QualityLine
            show
            text="List prices only: Meta's volume discounts on utility and authentication are not applied."
          />
        </ul>
      </ChartCard>
    </div>
  );
}

function QualityLine({ show, text, warn }: { show: boolean; text: string; warn?: boolean }) {
  if (!show) return null;
  const Icon = warn ? AlertTriangle : Info;
  return (
    <li className="flex gap-2">
      <Icon
        className={`mt-0.5 h-4 w-4 shrink-0 ${warn ? "text-amber-500" : "text-muted-foreground"}`}
      />
      <span className="text-muted-foreground">{text}</span>
    </li>
  );
}

interface CostRow {
  key: string;
  label: string;
  badge?: string;
  billable: number;
  then: number;
  next: number;
}

function CostTable({ head, rows }: { head: string; rows: CostRow[] }) {
  if (rows.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">No billed messages.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th className="py-2 font-medium">{head}</th>
            <th className="py-2 text-right font-medium">Billed</th>
            <th className="py-2 text-right font-medium">At the time</th>
            <th className="py-2 text-right font-medium">New pricing</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b last:border-0 align-top">
              <td className="py-2">
                <div className="flex flex-wrap items-center gap-2">
                  {r.label}
                  {r.badge && (
                    <Badge variant="outline" className="text-[10px] font-normal">
                      {r.badge}
                    </Badge>
                  )}
                </div>
              </td>
              <td className="py-2 text-right tabular-nums">
                {formatters.formatNumber(r.billable)}
              </td>
              <td className="py-2 text-right tabular-nums">{formatUsd(r.then)}</td>
              <td className="py-2 text-right font-medium tabular-nums">{formatUsd(r.next)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
